import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Headphones } from "lucide-react";
import StepHeader from "@/components/StepHeader";
import { readAdvisorState, resetAdvisorState } from "@/data/polyFitData";
import { useAdvisorContent } from "@/hooks";
import { trackAdvisorEvent } from "@/utils/analytics";

const BANNER_RATIO = 1917 / 797;
const BANNER_CTA = { left: 11.06, top: 55.3, width: 17, height: 8.1 };

function useDesktop() {
  const query = "(min-width: 900px)";
  const [desktop, setDesktop] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return desktop;
}

function HeroBanner({ hero, start }) {
  const wrapRef = useRef(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = wrapRef.current;
    const fit = () => {
      const cw = el.clientWidth, ch = el.clientHeight;
      setBox(cw / ch > BANNER_RATIO ? { width: ch * BANNER_RATIO, height: ch } : { width: cw, height: cw / BANNER_RATIO });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="hero-banner-wrap" ref={wrapRef} data-testid="cover-banner-wrap">
      <div className="hero-banner reveal-in" style={{ width: box.width, height: box.height }} data-testid="cover-banner">
        <img src={hero.bannerImageUrl} alt={`${hero.title} ${hero.titleAccent} — ${hero.subtitle}`} data-testid="cover-banner-image" />
        <Link className="hero-banner-cta" to="/question/workplace" onClick={start} aria-label={hero.ctaLabel} data-testid="cover-play-now-button" style={{ left: `${BANNER_CTA.left}%`, top: `${BANNER_CTA.top}%`, width: `${BANNER_CTA.width}%`, height: `${BANNER_CTA.height}%` }} />
      </div>
    </div>
  );
}

export default function FitLandingPage() {
  const { hero } = useAdvisorContent();
  const desktop = useDesktop();
  useEffect(() => {
    const state = resetAdvisorState();
    trackAdvisorEvent("how_it_works_view", { sessionId: state.sessionId, source: "landing_intro" });
  }, []);

  const start = () => trackAdvisorEvent("quiz_start", { sessionId: readAdvisorState().sessionId, source: "landing" });

  return (
    <main className="advisor-page landing-page" data-testid="cover-page">
      <StepHeader step={1} compact progressLabel="Find Your Poly Fit" />
      {desktop && hero.bannerImageUrl ? <HeroBanner hero={hero} start={start} /> : (
        <section className="advisor-hero hero-split" data-testid="cover-hero-section">
          <div className="hero-copy reveal-in" data-testid="cover-hero-copy">
            <p className="eyebrow" data-testid="cover-eyebrow">{hero.eyebrow}</p>
            <h1 data-testid="cover-title">{hero.title} <span>{hero.titleAccent}</span></h1>
            <p className="hero-subtitle" data-testid="cover-subtitle">{hero.subtitle}</p>
            <p className="hero-body" data-testid="cover-body">{hero.body}</p>
            <Link className="primary-cta" to="/question/workplace" onClick={start} data-testid="cover-play-now-button">
              <Headphones size={19} /> {hero.ctaLabel} <ArrowRight size={18} />
            </Link>
            <p className="gift-callout" data-testid="cover-gift-callout">Exciting gifts to be won!</p>
            <div className="certification-badge-strip" data-testid="cover-certification-badge-strip">
              <img src={hero.badgeImageUrl || "/certification-badges.png"} alt="Certified for Microsoft Teams, Zoom Certified, works with Google Meet, TCO Certified" data-testid="cover-certification-badges-image" />
            </div>
          </div>
          <div className="hero-visual" data-testid="cover-hero-visual">
            <img src={hero.backgroundImageUrl || "/home-hero.jpg"} alt="Professional on a video call wearing an HP Poly headset" data-testid="cover-hero-image" />
          </div>
        </section>
      )}
    </main>
  );
}
